import * as z from "zod";
import { TFunction } from "i18next";

// Reusable validators
const requiredString = (t: TFunction, key: string, message?: string) =>
  z.string().min(1, message || t(key));

export const createBaseAddressSchema = (t: TFunction) =>
  z.object({
    firstName: requiredString(t, "validation.firstNameRequired"),
    lastName: requiredString(t, "validation.lastNameRequired"),
    country: z.string().min(2, t("validation.countryRequired")),
    state: requiredString(t, "validation.cityRequired"),
    city: requiredString(t, "validation.districtRequired"),
    address1: requiredString(t, "validation.addressRequired"),
    // Changed: Postcode is now optional in the base schema structure
    // We will refine it based on country later
    postcode: z.string().optional(),
    phone: requiredString(t, "validation.phoneRequired"),
    address2: z.string().optional(),
    email: z.string().optional(), // Made optional here, refined in specific schemas
  });

// Shared Logic: Enforce Postcode logic (Required for non-TR)
const refinePostcode = (
  data: { country?: string; postcode?: string },
  ctx: z.RefinementCtx,
  t: TFunction,
) => {
  if (data.country !== "TR") {
    if (!data.postcode || data.postcode.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validation.postcodeRequired"),
        path: ["postcode"],
      });
    }
  }
};

export const createBillingSchema = (t: TFunction) => {
  return createBaseAddressSchema(t)
    .extend({
      email: z.string().email(t("validation.emailInvalid")),
      customerType: z.enum(["individual", "corporate"]),
      company: z.string().optional(),
      tckn: z.string().optional(),
      vkn: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // 1. Postcode Logic
      refinePostcode(data, ctx, t);

      // 2. Company Validation (Required for Corporate, regardless of country)
      if (data.customerType === "corporate") {
        if (!data.company || data.company.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              "validation.companyRequired",
              "Company title is required",
            ),
            path: ["company"],
          });
        }
      }

      // 3. Turkish Specific Validation (TCKN / VKN)
      // Only apply these strict rules if the country is TR
      if (data.country === "TR") {
        // Feature Flag: Check if TCKN is enabled globally
        const enableTckn = window.commonVariables?.enableTckn ?? true;

        if (data.customerType === "individual") {
          // Only validate TCKN if the feature is enabled
          if (enableTckn) {
            // TCKN is now optional. We only validate the format if a value exists.
            if (
              data.tckn &&
              data.tckn.length > 0 &&
              !/^\d{11}$/.test(data.tckn)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("validation.tcknInvalid", "TCKN must be 11 digits"),
                path: ["tckn"],
              });
            }
          }
        } else {
          // Corporate checks for TR: VKN remains required regardless of TCKN setting
          if (!data.vkn || !/^\d{10}$/.test(data.vkn)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("validation.vknInvalid", "VKN must be 10 digits"),
              path: ["vkn"],
            });
          }
        }
      }
    });
};

export const createShippingSchema = (t: TFunction) => {
  return createBaseAddressSchema(t)
    .extend({
      // Add placeholders for types that might exist in the form data but aren't validated
      company: z.string().optional(),
      customerType: z.enum(["individual", "corporate"]).optional(),
      tckn: z.string().optional(),
      vkn: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // Apply Postcode logic here as well
      refinePostcode(data, ctx, t);
    });
};

export type BillingFormData = z.infer<ReturnType<typeof createBillingSchema>>;
export type ShippingFormData = z.infer<ReturnType<typeof createShippingSchema>>;

export const createAppointmentSchema = (t: TFunction) =>
  z.object({
    date: z.string().min(1, t("validation.required", "Please select a date.")),
    time: z.string().min(1, t("validation.required", "Please select a time.")),
  });

export type AppointmentFormData = z.infer<
  ReturnType<typeof createAppointmentSchema>
>;
